import type { IntegrationType } from './types';

/**
 * Helpdesks belong to the ACCOUNT; every other integration belongs to a brand.
 *
 * One Gorgias workspace serves every brand an account runs — brands are separated inside
 * it by the inbox a ticket lands on, which the control panel maps. So a helpdesk card has
 * no brand to choose, and offering one would ask the operator a question with no true
 * answer.
 *
 * Every other integration is the opposite: two brands genuinely run two Shopify stores or
 * two Recharge accounts, and connecting one has to say which brand it belongs to.
 *
 * kim-tool-server enforces the same split where credentials are chosen and machine where
 * they are written; this file is only about what the page offers. Keeping the three in
 * step matters — a card that offers a brand the backend will discard is worse than one
 * that never offered it.
 */
const HELPDESK_INTEGRATIONS = new Set<string>([
	'gorgias',
	// The older database spelling, still what dev rows carry.
	'gorgias_ai',
	'zendesk',
	'freshdesk',
	'kustomer',
	'richpanel',
	'gladly',
	'intercom',
	'helpscout',
	'zoho',
	// How integrations_platforms.name spells Zoho; `zoho` is the catalog id.
	'zohodesk',
	// Kim's built-in helpdesk. Connects alongside one external helpdesk.
	'helpdesk'
]);

/**
 * Kim's built-in helpdesk is always the SECONDARY helpdesk: it runs next to the account's one
 * external (primary) helpdesk — e.g. Gorgias for email, Kim Helpdesk for live chat — and never
 * takes or blocks the primary's place.
 */
export const KIM_HELPDESK = 'helpdesk';
export const isKimHelpdesk = (id: string): boolean => String(id).toLowerCase() === KIM_HELPDESK;

/** Whether this integration's connection belongs to the account as a whole. */
export const isAccountScopedIntegration = (id: IntegrationType | string): boolean =>
	HELPDESK_INTEGRATIONS.has(String(id).toLowerCase());

/** Database spellings of a helpdesk that the backend's helpdeskProvider spells differently. */
const HELPDESK_ALIASES: Record<string, string> = { gorgias_ai: 'gorgias', zohodesk: 'zoho' };

/** The account's connected helpdesks, newest first, deduplicated, spelled the way the backend's helpdeskProvider is. */
export const connectedHelpdesks = (
	integrations: ReadonlyArray<{ id: number | string; name: string; isConnected?: boolean }>
): string[] => [
	...new Set(
		integrations
			.filter((i) => i.isConnected && isAccountScopedIntegration(i.name))
			.sort((a, b) => Number(b.id) - Number(a.id))
			.map((i) => HELPDESK_ALIASES[i.name.toLowerCase()] ?? i.name.toLowerCase())
	)
];
